import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering,
  CatalogService,
  CatalogWarehouse,
  CatalogWarehouseBin,
  CatalogWarehouseZone,
  InventoryLedgerEntry,
  InventoryStockBalance
} from './catalog.service';

type WarehouseTab = 'general' | 'zones' | 'bins' | 'balances' | 'ledger';

@Component({
  standalone: true,
  selector: 'ain-inventory-warehouse-detail-v2',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, CheckboxModule, InputTextModule, SelectModule, TableModule],
  template: `
    <ain-page [title]="isCreateMode() ? t('catalog.warehouses.createTitle') : t('catalog.warehouses.detailTitle')" [description]="t('catalog.warehouses.detailDescription')">
      <ain-inventory-page-hint pageKey="warehouseDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="head">
          <h3>{{ form.name || t('catalog.warehouses.newRecord') }}</h3>
          <div class="actions">
            <a [routerLink]="['/inventory/warehouses']">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.a]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          <button type="button" [class.a]="activeTab() === 'zones'" [disabled]="isCreateMode()" (click)="activeTab.set('zones')">{{ t('catalog.tabs.zones') }}</button>
          <button type="button" [class.a]="activeTab() === 'bins'" [disabled]="isCreateMode()" (click)="activeTab.set('bins')">{{ t('catalog.tabs.bins') }}</button>
          <button type="button" [class.a]="activeTab() === 'balances'" [disabled]="isCreateMode()" (click)="activeTab.set('balances')">{{ t('catalog.tabs.balances') }}</button>
          <button type="button" [class.a]="activeTab() === 'ledger'" [disabled]="isCreateMode()" (click)="activeTab.set('ledger')">{{ t('catalog.tabs.ledger') }}</button>
        </section>

        @if (activeTab() === 'general') {
          <section class="grid">
            <label><span>{{ t('columns.title') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label><span>{{ t('columns.code') }}</span><input pInputText [(ngModel)]="form.code" /></label>
            <label class="check"><p-checkbox [binary]="true" [(ngModel)]="form.isActive" /><span>{{ t('catalog.warehouses.active') }}</span></label>
          </section>
        }

        @if (activeTab() === 'zones') {
          <section class="grid mini">
            <label><span>{{ t('columns.title') }}</span><input pInputText [(ngModel)]="zoneForm.name" /></label>
            <label><span>{{ t('columns.code') }}</span><input pInputText [(ngModel)]="zoneForm.code" /></label>
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="createZone()"></button>
          </section>
          <p-table [value]="zones()">
            <ng-template pTemplate="header"><tr><th>{{ t('columns.code') }}</th><th>{{ t('columns.title') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.code }}</td><td>{{ row.name }}</td></tr></ng-template>
          </p-table>
        }

        @if (activeTab() === 'bins') {
          <section class="grid mini">
            <label><span>{{ t('columns.title') }}</span><input pInputText [(ngModel)]="binForm.name" /></label>
            <label><span>{{ t('columns.code') }}</span><input pInputText [(ngModel)]="binForm.code" /></label>
            <label>
              <span>{{ t('catalog.warehouses.binType') }}</span>
              <p-select [(ngModel)]="binForm.binType" [options]="binTypeOptions" optionLabel="label" optionValue="value" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.warehouses.zoneOptional') }}</span>
              <p-select [(ngModel)]="binForm.zoneId" [options]="zoneOptions()" optionLabel="label" optionValue="value" appendTo="body" />
            </label>
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="createBin()"></button>
          </section>
          <p-table [value]="bins()">
            <ng-template pTemplate="header"><tr><th>{{ t('columns.code') }}</th><th>{{ t('columns.title') }}</th><th>{{ t('catalog.warehouses.zone') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.code }}</td><td>{{ row.name }}</td><td>{{ zoneName(row.zoneId) }}</td></tr></ng-template>
          </p-table>
        }

        @if (activeTab() === 'balances') {
          <p-table [value]="warehouseBalances()">
            <ng-template pTemplate="header"><tr><th>{{ t('catalog.inventory.offeringId') }}</th><th>{{ t('catalog.inventory.binId') }}</th><th>{{ t('catalog.inventory.quantity') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ resolveOfferingLabel(row.offeringId) }}</td><td>{{ row.binId || '-' }}</td><td>{{ row.quantity }}</td></tr></ng-template>
          </p-table>
        }

        @if (activeTab() === 'ledger') {
          <p-table [value]="warehouseLedger()">
            <ng-template pTemplate="header"><tr><th>{{ t('catalog.inventory.operation') }}</th><th>{{ t('catalog.inventory.reference') }}</th><th>{{ t('catalog.inventory.delta') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row><tr><td>{{ row.operationType }}</td><td>{{ row.referenceNumber }}</td><td>{{ row.quantityDelta }}</td></tr></ng-template>
          </p-table>
        }
      }
    </ain-page>
  `,
  styles: [`
    .head,.actions,.tabs{display:flex;gap:8px;align-items:center;flex-wrap:wrap}.head{justify-content:space-between}
    .tabs button{border:1px solid #cbd5e1;border-radius:999px;background:#fff;padding:6px 12px}.tabs .a{border-color:#1d4ed8;color:#1d4ed8;font-weight:700}
    .grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;margin:10px 0}.grid label{display:grid;gap:6px}.check{display:inline-flex;align-items:center;gap:8px}
  `]
})
export class InventoryWarehouseDetailV2Component implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly activeTab = signal<WarehouseTab>('general');
  readonly isCreateMode = signal(false);
  readonly warehouse = signal<CatalogWarehouse | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly allBalances = signal<InventoryStockBalance[]>([]);
  readonly allLedger = signal<InventoryLedgerEntry[]>([]);
  readonly zones = signal<CatalogWarehouseZone[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  readonly warehouseBalances = computed(() => this.allBalances().filter(i => i.warehouseId === this.warehouse()?.id));
  readonly warehouseLedger = computed(() => this.allLedger().filter(i => i.warehouseId === this.warehouse()?.id));
  readonly form = { name: '', code: '', isActive: true };
  readonly zoneForm = { name: '', code: '' };
  readonly binForm = { name: '', code: '', zoneId: '', binType: 'Standard' };
  readonly binTypeOptions = [
    { label: 'Standard', value: 'Standard' },
    { label: 'Quarantine', value: 'Quarantine' },
    { label: 'Staging', value: 'Staging' }
  ];

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) { this.loading.set(false); return; }
    if (id === 'new') { this.isCreateMode.set(true); this.loading.set(false); return; }
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(id, false)
    );
    this.load(id);
  }

  save(): void {
    if (!this.form.name.trim() || !this.form.code.trim()) return;
    if (this.isCreateMode()) {
      this.service.createWarehouse({ name: this.form.name.trim(), code: this.form.code.trim() }).subscribe({ next: created => void this.router.navigate(['/inventory/warehouses', created.id]) });
      return;
    }
    const id = this.warehouse()?.id;
    if (!id) return;
    this.service.updateWarehouse(id, { name: this.form.name.trim(), code: this.form.code.trim(), isActive: this.form.isActive }).subscribe({ next: updated => this.warehouse.set(updated) });
  }

  createZone(): void {
    const warehouseId = this.warehouse()?.id;
    if (!warehouseId || !this.zoneForm.name.trim() || !this.zoneForm.code.trim()) return;
    this.service.createWarehouseZone({ warehouseId, name: this.zoneForm.name.trim(), code: this.zoneForm.code.trim() }).subscribe({ next: () => this.loadZonesBins(warehouseId) });
  }

  createBin(): void {
    const warehouseId = this.warehouse()?.id;
    if (!warehouseId || !this.binForm.name.trim() || !this.binForm.code.trim()) return;
    this.service.createWarehouseBin({
      warehouseId,
      zoneId: this.binForm.zoneId || null,
      name: this.binForm.name.trim(),
      code: this.binForm.code.trim(),
      binType: this.binForm.binType
    }).subscribe({ next: () => this.loadZonesBins(warehouseId) });
  }

  zoneOptions(): Array<{ label: string; value: string }> {
    return [{ label: this.t('catalog.warehouses.noZone'), value: '' }, ...this.zones().map(z => ({ label: `${z.code} - ${z.name}`, value: z.id }))];
  }

  zoneName(zoneId: string | null): string {
    if (!zoneId) return this.t('catalog.warehouses.noZone');
    const zone = this.zones().find(z => z.id === zoneId);
    return zone ? `${zone.code} - ${zone.name}` : zoneId;
  }

  resolveOfferingLabel(offeringId: string | null | undefined): string {
    if (!offeringId) return '-';
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  private load(id: string, showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.getWarehouse(id).subscribe({
      next: warehouse => {
        this.warehouse.set(warehouse);
        this.form.name = warehouse.name;
        this.form.code = warehouse.code;
        this.form.isActive = warehouse.isActive;
        this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows), error: () => this.offerings.set([]) });
        this.service.listStockBalances().subscribe({ next: rows => this.allBalances.set(rows) });
        this.service.listLedger().subscribe({ next: rows => this.allLedger.set(rows) });
        this.loadZonesBins(id, () => this.loading.set(false));
      },
      error: () => this.loading.set(false)
    });
  }

  private loadZonesBins(warehouseId: string, done?: () => void): void {
    this.service.listWarehouseZones(warehouseId).subscribe({
      next: zones => {
        this.zones.set(zones);
        this.service.listWarehouseBins(warehouseId).subscribe({
          next: bins => { this.bins.set(bins); done?.(); },
          error: () => done?.()
        });
      },
      error: () => done?.()
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
